import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, Crown, Mail, Lock, ArrowRight, Phone } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "/doslions-logo.png";

export default function AuthPage() {
  const [mode, setMode] = useState("login"); // login, register, forgot, reset
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    resetToken: "",
    newPassword: ""
  });
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password
      });
      login(response.data.access_token, response.data.user);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Invalid credentials";
      toast.error(typeof message === 'string' ? message : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Register creates account and redirects to payment
      const response = await axios.post(`${API}/auth/register`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone
      });
      login(response.data.access_token, response.data.user);
      toast.success("Account created! Complete your Pro subscription.");
      // Redirect to subscription page for payment
      navigate("/subscription");
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed";
      toast.error(typeof message === 'string' ? message : "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: formData.email });
      setResetSent(true);
      toast.success("Recovery email sent! Check your inbox.");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send recovery email";
      toast.error(typeof message === 'string' ? message : "Error sending email");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: formData.resetToken,
        new_password: formData.newPassword
      });
      toast.success("Password reset successfully! Please login.");
      setMode("login");
      setFormData({ ...formData, resetToken: "", newPassword: "" });
    } catch (error) {
      const message = error.response?.data?.detail || "Invalid or expired reset token";
      toast.error(typeof message === 'string' ? message : "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 grid-pattern">
      <div className="w-full max-w-md">
        {/* Back button */}
        <button 
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-zinc-500 hover:text-amber-500 mb-8 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </button>

        {/* Auth Card */}
        <div className="bg-[#111111] border border-zinc-800 p-8">
          <div 
            className="flex items-center justify-center gap-3 mb-8 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate("/")}
            data-testid="logo-home-link"
          >
            <img src={LOGO_URL} alt="ECOLIONS" className="w-12 h-12 object-contain" />
            <div>
              <span className="font-bold text-2xl tracking-tight text-amber-500" style={{ fontFamily: 'Barlow Condensed' }}>
                ECOLIONS
              </span>
              <span className="font-bold text-2xl tracking-tight text-white ml-1" style={{ fontFamily: 'Barlow Condensed' }}>
                AI
              </span>
            </div>
          </div>

          {/* LOGIN MODE */}
          {mode === "login" && (
            <>
              <h1 className="text-3xl font-bold text-center mb-2 uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                Welcome Back
              </h1>
              <p className="text-center text-zinc-500 text-sm mb-8">
                Enter your credentials to access your account
              </p>

              <form onSubmit={handleLogin} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-400">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-[#0A0A0A] border-zinc-800 focus:border-amber-500 h-12"
                    required
                    data-testid="email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-400">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="bg-[#0A0A0A] border-zinc-800 focus:border-amber-500 h-12 pr-10"
                      required
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-amber-500"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Forgot Password Link */}
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => setMode("forgot")}
                    className="text-xs text-amber-500 hover:text-amber-400 transition-colors"
                    data-testid="forgot-password-btn"
                  >
                    Forgot password?
                  </button>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider py-6 shadow-[0_0_20px_rgba(245,158,11,0.3)]"
                  disabled={loading}
                  data-testid="login-btn"
                >
                  {loading ? "Loading..." : "Sign In"}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setMode("register")}
                  className="text-sm text-zinc-500 hover:text-amber-500 transition-colors"
                  data-testid="toggle-register-btn"
                >
                  Don't have an account? <span className="text-amber-500">Sign up</span>
                </button>
              </div>
            </>
          )}

          {/* REGISTER MODE - Pro Ecopreneur Only */}
          {mode === "register" && (
            <>
              <div className="flex items-center justify-center gap-2 mb-4">
                <Crown className="w-6 h-6 text-amber-500" />
                <h1 className="text-2xl font-bold text-center uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                  Become a <span className="text-amber-500">Pro Ecopreneur</span>
                </h1>
              </div>
              
              {/* Pro Benefits Banner */}
              <div className="bg-amber-500/10 border border-amber-500/30 p-4 mb-6">
                <p className="text-amber-500 text-sm font-bold mb-2">What you'll get:</p>
                <ul className="text-xs text-zinc-400 space-y-1">
                  <li className="flex items-center gap-2">
                    <ArrowRight className="w-3 h-3 text-amber-500" /> AI Hot Picks — Daily BUY/SELL Signals
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRight className="w-3 h-3 text-amber-500" /> Top Market Movers & Whale Tracker
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRight className="w-3 h-3 text-amber-500" /> AI Earnings Whisper & Social Sentiment
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRight className="w-3 h-3 text-amber-500" /> Economic Calendar AI & Portfolio Tools
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRight className="w-3 h-3 text-amber-500" /> 500+ Assets: Stocks, Crypto, Commodities, Forex
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRight className="w-3 h-3 text-amber-500" /> Priority Support
                  </li>
                </ul>
                <p className="text-amber-500 font-bold mt-3 text-center">
                  $19.97 first month • Then $49.97/mo
                </p>
              </div>

              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-xs uppercase tracking-wider text-zinc-400">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-[#0A0A0A] border-zinc-800 focus:border-amber-500 h-12"
                    required
                    data-testid="name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-400">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-[#0A0A0A] border-zinc-800 focus:border-amber-500 h-12"
                    required
                    data-testid="email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-xs uppercase tracking-wider text-zinc-400">
                    Phone <span className="text-zinc-600">(optional)</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-[#0A0A0A] border-zinc-800 focus:border-amber-500 h-12"
                    data-testid="phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-400">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="bg-[#0A0A0A] border-zinc-800 focus:border-amber-500 h-12 pr-10"
                      required
                      minLength={6}
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-amber-500"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider py-6 shadow-[0_0_20px_rgba(245,158,11,0.3)]"
                  disabled={loading}
                  data-testid="register-btn"
                >
                  <Crown className="w-5 h-5 mr-2" />
                  {loading ? "Creating Account..." : "Continue to Payment"}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setMode("login")}
                  className="text-sm text-zinc-500 hover:text-amber-500 transition-colors"
                  data-testid="toggle-login-btn"
                >
                  Already have an account? <span className="text-amber-500">Sign in</span>
                </button>
              </div>
            </>
          )}

          {/* FORGOT PASSWORD MODE */}
          {mode === "forgot" && (
            <>
              <div className="flex items-center justify-center gap-2 mb-4">
                <Mail className="w-6 h-6 text-amber-500" />
                <h1 className="text-2xl font-bold text-center uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                  Reset Password
                </h1>
              </div>
              
              {!resetSent ? (
                <>
                  <p className="text-center text-zinc-500 text-sm mb-6">
                    Enter your email and we'll send you a recovery code
                  </p>

                  <form onSubmit={handleForgotPassword} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-400">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="bg-[#0A0A0A] border-zinc-800 focus:border-amber-500 h-12"
                        required
                        data-testid="forgot-email-input"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider py-6"
                      disabled={loading}
                      data-testid="send-reset-btn"
                    >
                      {loading ? "Sending..." : "Send Recovery Code"}
                    </Button>
                  </form>
                </>
              ) : (
                <>
                  <p className="text-center text-emerald-500 text-sm mb-6">
                    Check your email for the recovery code!
                  </p>
                  <Button 
                    onClick={() => setMode("reset")}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider py-6"
                    data-testid="enter-code-btn"
                  >
                    Enter Recovery Code
                  </Button>
                </>
              )}

              <div className="mt-6 text-center">
                <button
                  onClick={() => { setMode("login"); setResetSent(false); }}
                  className="text-sm text-zinc-500 hover:text-amber-500 transition-colors"
                >
                  Back to login
                </button>
              </div>
            </>
          )}

          {/* RESET PASSWORD MODE */}
          {mode === "reset" && (
            <>
              <div className="flex items-center justify-center gap-2 mb-4">
                <Lock className="w-6 h-6 text-amber-500" />
                <h1 className="text-2xl font-bold text-center uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                  Set New Password
                </h1>
              </div>
              
              <p className="text-center text-zinc-500 text-sm mb-6">
                Enter the code from your email and your new password
              </p>

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="token" className="text-xs uppercase tracking-wider text-zinc-400">Recovery Code</Label>
                  <Input
                    id="token"
                    type="text"
                    placeholder="Enter code from email"
                    value={formData.resetToken}
                    onChange={(e) => setFormData({ ...formData, resetToken: e.target.value })}
                    className="bg-[#0A0A0A] border-zinc-800 focus:border-amber-500 h-12 text-center tracking-widest"
                    required
                    data-testid="reset-token-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-xs uppercase tracking-wider text-zinc-400">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.newPassword}
                      onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                      className="bg-[#0A0A0A] border-zinc-800 focus:border-amber-500 h-12 pr-10"
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-amber-500"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider py-6"
                  disabled={loading}
                  data-testid="reset-password-btn"
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => { setMode("login"); setResetSent(false); }}
                  className="text-sm text-zinc-500 hover:text-amber-500 transition-colors"
                >
                  Back to login
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-zinc-600 mt-6">
          By continuing, you agree to our Terms of Service
        </p>
      </div>
    </div>
  );
}
